import { isAuthenticated, logout } from "@/_services/authentication"
import consoleService from "@/_services/console"
import { loc } from "@/_services/localization"
import { isOffline } from "@/_services/offlineService.js"
import {
  addSessionSealKey,
  bindSessionSeal,
  forgetSessionSeal,
  hasIssuedSessionSeal,
  hasSessionSeal,
  isSessionSealSupported,
  loadSessionSeal,
  mayNeedSessionSeal,
  prepareSessionSealKey,
  resetSessionSeal,
  resetSessionSealRequest,
  saveIssuedSessionSeal,
  sealSessionRequest,
  sessionSealStatus,
  unsealSessionEventData,
  unsealSessionResponse,
  unsealSessionResponseText,
  updateSessionSealClock,
} from "@/_services/sessionSeal"
import { hideSpinner, showSpinner } from "@/_services/spinner"
import { getHostConfig } from "@/_services/theming"
import { debug, httpHeaders, localStorageKeys } from "@/_services/utils"
import axios from "axios"
import { convertDateStringsToDates, requestOriginators } from "basikon-common-utils"
import { v4 as uuidv4 } from "uuid"

let headerAuthorization
let userExternalContexts
let headerContextToken

// Storage event listener to synchronize SSO tokens across browser tabs
window.addEventListener("storage", event => {
  const { memoizeToken } = getHostConfig()
  if (!memoizeToken) return

  // Synchronize Authorization header when updated in another tab
  if (event.key === localStorageKeys.HEADER_AUTH) {
    // The session seal of the new token is read again from the database
    forgetSessionSeal()
    if (event.newValue) {
      headerAuthorization = event.newValue
    } else if (event.newValue === null) {
      // Token was removed (logout in another tab)
      headerAuthorization = undefined
    }
  }

  // Synchronize Context tokens (for tenants using external contexts)
  if (event.key === localStorageKeys.HEADER_USER_CONTEXTS) {
    userExternalContexts = event.newValue
  }
  if (event.key === localStorageKeys.HEADER_CONTEXT_TOKEN) {
    headerContextToken = event.newValue
  }
})

export function getHeaderAuthorization() {
  const { memoizeToken } = getHostConfig()

  if (memoizeToken) {
    const isRefreshing = localStorage.getItem(localStorageKeys.OIDC?.IS_ATTEMPTING_REFRESH)
    if (isRefreshing) {
      const storedToken = localStorage.getItem(localStorageKeys.HEADER_AUTH)
      // Update in-memory token if storage has a newer one
      if (storedToken && storedToken !== headerAuthorization) {
        headerAuthorization = storedToken
      }
      return headerAuthorization || storedToken
    }
  }

  return headerAuthorization || (memoizeToken ? localStorage.getItem(localStorageKeys.HEADER_AUTH) : undefined)
}

export function getHeaderUserExternalContexts() {
  const { memoizeToken } = getHostConfig()
  return userExternalContexts || (memoizeToken ? localStorage.getItem(localStorageKeys.HEADER_USER_CONTEXTS) : undefined)
}

export function getHeaderContextToken() {
  const { memoizeToken } = getHostConfig()
  return headerContextToken || (memoizeToken ? localStorage.getItem(localStorageKeys.HEADER_CONTEXT_TOKEN) : undefined)
}

export function setHeaderAuthorization(auth) {
  headerAuthorization = auth
  const { memoizeToken } = getHostConfig()
  if (memoizeToken) localStorage.setItem(localStorageKeys.HEADER_AUTH, auth)
}

//When we want to use external extra context
export function setHeaderUserExternalContexts(userContextsHeaderValue, contextTokenHeaderValue) {
  userExternalContexts = userContextsHeaderValue
  headerContextToken = contextTokenHeaderValue

  const { memoizeToken } = getHostConfig()
  if (memoizeToken) {
    localStorage.setItem(localStorageKeys.HEADER_USER_CONTEXTS, userContextsHeaderValue)
    localStorage.setItem(localStorageKeys.HEADER_CONTEXT_TOKEN, contextTokenHeaderValue)
  }
}

export function setUserCurrentContextIndex(userContextIndex) {
  localStorage.setItem(localStorageKeys.USER_CURRENT_CONTEXT_INDEX, userContextIndex)
}

export function getUserCurrentContextIndex() {
  return localStorage.getItem(localStorageKeys.USER_CURRENT_CONTEXT_INDEX)
}

export function resetHeaderAuthorization() {
  headerAuthorization = undefined
  userExternalContexts = undefined
  headerContextToken = undefined
  localStorage.removeItem(localStorageKeys.HEADER_AUTH)
  localStorage.removeItem(localStorageKeys.HEADER_CONTEXT_TOKEN)
  localStorage.removeItem(localStorageKeys.HEADER_USER_CONTEXTS)
  resetSessionSeal()
}

export function getAuthorizationToken() {
  return getHeaderAuthorization()?.substring("bearer ".length)
}

export function formatAxiosError(error) {
  if (error) {
    console.error(
      "====",
      error,
      error.response,
      error.response && error.response.data,
      error.response && error.response.data && error.response.data.message,
    )
  }

  let msg
  if (error.response && error.response.data) {
    msg = debug ? error.response.data.stack : error.response.data.message || error.response.data
    const data = error.response.data
    if (debug && data.stack) {
      msg = data.stack
    } else {
      const array = data.strings
      msg = array ? loc(data.message, ...array) : loc(data.overriddenMessage || data.message || data.errors?.join(", ") || data)
    }
    msg += " (" + error.response.status + ")"
  }
  if (!msg) msg = debug && error.stack ? error.stack : error.message
  if (msg === "Network Error") {
    if (isOffline()) {
      msg = loc("You are in offline mode, some features requiring network connectivity are not available.")
    } else {
      msg = loc("Network error") + ": " + loc("Check your internet connection")
    }
  }
  return loc(msg)
}

function handleResponse(response, isSameOrigin) {
  convertDateStringsToDates(response.data)
  // The token of a session binding is stored by the binding itself, once the session is saved (see sessionSeal.js)
  if (isSameOrigin && !response.config.sessionSealBinding) {
    const headers = response.headers
    if (headers && (headers.hasOwnProperty("Authorization") || headers.hasOwnProperty("authorization"))) {
      const auth = headers && (headers.Authorization || headers.authorization)
      // If the authorization header is returned it can be empty (an empty string), when calling logout for instance.
      // In this case although using setHeaderAuthorization works, as it would keep the key headerAuthorization but set an empty value,
      // it is cleaner to remove the completely the remove the key.
      if (auth) {
        setHeaderAuthorization(auth)
      } else {
        resetHeaderAuthorization()
      }
    }
  }
  if (!response.config.skipSpinner) hideSpinner()
  if (consoleService.showAxiosInterceptors) console.log("End calling NO error", response.config.url, "(" + response.status + ")")
  return response
}

// The login APIs (and the other APIs issuing tokens) are in core
function isCoreApi(url, baseUrl) {
  const path = url.startsWith(baseUrl) ? url.substring(baseUrl.length) : url
  return path.startsWith("/api/core/")
}

/**
 * Session seal: the sealed responses are unsealed, and the session of a token bound by a login API is saved before the token is stored.
 * Asynchronous only in these cases, so that the other responses are handled as before.
 */
function unsealResponse(response) {
  if (hasIssuedSessionSeal(response)) return saveIssuedSessionSeal(response)
  if (response.config.sessionSeal) return unsealSessionResponse(response)
}

/**
 * A request refused because of the session seal (428): the session is bound (or bound again), then the request is sent again,
 * only once. When the binding is refused, the user is logged out.
 */
async function retryWithSessionSeal(error) {
  // Without WebCrypto (not HTTPS) the session can't be bound: the error says so instead of logging out in a loop
  if (!isSessionSealSupported()) {
    error.response.data = { message: "A secure connection (HTTPS) is required" }
    return Promise.reject(error)
  }

  const { config } = error
  const authorization = await bindSessionSeal()
  if (authorization) {
    setHeaderAuthorization(authorization)
    config.sessionSealRetried = true
    resetSessionSealRequest(config)
    return axios(config)
  }

  if (isAuthenticated()) await logout({ errorMessage: "Session has expired" })
  return Promise.reject(error)
}

export function setupAxiosClient(baseUrl) {
  function sameOrigin(url) {
    return url.startsWith("/api") || url.startsWith(baseUrl)
  }

  headerAuthorization = getHeaderAuthorization()
  loadSessionSeal()
  prepareSessionSealKey()

  axios.interceptors.request.use(
    function (config) {
      if (consoleService.showAxiosInterceptors) console.log("Calling", config.method.toUpperCase(), config.url)
      if (!config.skipSpinner) showSpinner()
      if (sameOrigin(config.url) && headerAuthorization) config.headers.Authorization = headerAuthorization

      // if both headers are set axios will concatenate them
      // resulting in an invalid token from the server point of view
      if (config.headers.Authorization && config.headers.authorization) delete config.headers.authorization

      config.headers[httpHeaders.requestOriginator] = requestOriginators.basikonClient
      config.headers[httpHeaders.requestId] = uuidv4() // for idempotent requests
      config.headers[httpHeaders.clientTimezone] = Intl.DateTimeFormat().resolvedOptions().timeZone

      // Without bound session, a token issued by the response (login) is bound at once (see sessionSeal.js)
      if (sameOrigin(config.url) && isCoreApi(config.url, baseUrl) && !hasSessionSeal()) {
        addSessionSealKey(config)
      }

      // Only when the session is (or may be) sealed: otherwise the interceptor stays synchronous
      if (sameOrigin(config.url) && mayNeedSessionSeal(config)) {
        // Not sealed (error): sent without proof, the server answers 428 and the session is bound again
        return sealSessionRequest(config).then(
          () => config,
          () => config,
        )
      }

      return config
    },
    function (error) {
      hideSpinner()
      return Promise.reject(error)
    },
  )

  axios.interceptors.response.use(
    function (response) {
      const isSameOrigin = sameOrigin(response.request.responseURL)
      if (isSameOrigin) updateSessionSealClock(response.headers?.date)

      const unsealing = isSameOrigin ? unsealResponse(response) : undefined
      if (!unsealing) return handleResponse(response, isSameOrigin)

      return unsealing.then(
        () => handleResponse(response, isSameOrigin),
        error => {
          if (!response.config.skipSpinner) hideSpinner()
          throw error
        },
      )
    },
    async function (error) {
      //debugger
      const { config } = error
      if (!config?.skipSpinner) hideSpinner()
      if (!error.response) {
        if (consoleService.showAxiosInterceptors) {
          console.log("End calling WITH error", error.config && error.config.url)
          console.log("Error:", error)
        }
      } else {
        if (consoleService.showAxiosInterceptors) {
          console.log("End calling WITH error", error.config && error.config.url, "(" + error.response.status + ")")
          console.log("Response data:", error.response.data)
        }
        const isSameOrigin = sameOrigin(error.response.request.responseURL)
        if (isSameOrigin) {
          updateSessionSealClock(error.response.headers?.date)
          try {
            await unsealSessionResponse(error.response)
          } catch {
            // The error is given with its sealed data
          }
        }

        const isSessionSealRetryable = error.response.status === sessionSealStatus && !config.sessionSealBinding && !config.sessionSealRetried
        if (isSameOrigin && isSessionSealRetryable) return retryWithSessionSeal(error)

        if (isSameOrigin && error.response.status === 401) {
          // We don't trigger the logout in any case because users can use the app without being connected,
          // notably in flow mode, so we have to precisely declare which cases we want to handle.
          // The OIDC case happens when users that were logged in through OIDC are hard reloading the app
          // and the platform token (from local storage) is invalid.
          // They are not authenticated anymore regarding the platform, but might still have a valid OIDC session.
          if (isAuthenticated()) {
            await logout({ errorMessage: "Session has expired" })
          }
        }
      }
      return Promise.reject(error)
    },
  )
}

/**
 * fetch for the APIs of the platform when axios can't be used (reading server-sent events): proof and sealed body
 * of the session seal, and a new binding when the server asks for it (then sent again, only once).
 * The body of the response is read with readText, the data of the server-sent events with unsealEventData when defined.
 */
export async function fetchApi(url, { method = "GET", headers = {}, body } = {}) {
  let response
  let config

  for (let attempt = 1; attempt <= 2; attempt++) {
    // The Authorization given by the caller, then the one of the new binding
    const authorization = attempt === 1 ? headers.Authorization : getHeaderAuthorization()
    config = { method, url, data: body, headers: { ...headers, ...(authorization ? { Authorization: authorization } : {}) } }
    if (mayNeedSessionSeal(config)) await sealSessionRequest(config)

    const isJsonBody = config.data && typeof config.data === "object" && !(config.data instanceof FormData)
    response = await fetch(url, { method, headers: config.headers, body: isJsonBody ? JSON.stringify(config.data) : config.data })
    updateSessionSealClock(response.headers?.get?.("date"))

    if (response.status !== sessionSealStatus || attempt === 2 || !isSessionSealSupported()) break

    const newAuthorization = await bindSessionSeal()
    if (!newAuthorization) break
    setHeaderAuthorization(newAuthorization)
  }

  const { sessionSeal } = config
  return {
    response,
    readText: async () => await unsealSessionResponseText(sessionSeal, await response.text()),
    // Only for a sealed request, whose events are sealed
    unsealEventData: sessionSeal ? async data => await unsealSessionEventData(sessionSeal, data) : undefined,
  }
}
